import { useDispatch } from "react-redux";
import { useApolloClient } from "lib/apollo/client";
import {
  deleteUserBook as destroyUserBook,
  insertUserBook,
  updateUserBook,
} from "services/UserBookService/UserBookServiceClient";
import {
  ReadStatus,
  WantToReadStatus,
} from "components/shared/UserBookStatuses";
import { PrivacySettingType } from "constants/PrivacySettings";
import { toast } from "sonner";
import useCurrentUser from "hooks/fragments/useCurrentUser";
import useCurrentUserId from "hooks/user/useCurrentUserId";
import { useUserBook } from "store/books/booksSelector";
import { setUserBook, removeUserBook, setUserBookRead } from "store/books/booksSlice";
import { bookButtonActions } from "store/bookButton/bookButtonSlice";
import { UserBookSerializersUserBook } from "types/serializers";
import { insertUserBookRead } from "services/UserBookReadService";

const catchErrorMessage = (error: string) => {
  toast.error(`Something went wrong: ${error}`);
  return error;
};

export default function useUserBookService(bookId: number, settings?: { referrerUserId?: number }) {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const defaultPrivacySettingId = useCurrentUser(
    (u) => u?.accountPrivacySettingId || 1
  );
  const userBook = useUserBook(bookId);

  async function updateUserBookCache(newUserBook: UserBookSerializersUserBook | null) {
    if (newUserBook) {
      dispatch(setUserBook({ userBook: newUserBook }));
      dispatch(bookButtonActions.setUserBook({ userBook: newUserBook }));
      
    }
    return newUserBook;
  }

  const insertDateRead = () => {
    if(userBook) {
      // Insert
      const promise = insertUserBookRead({
        client,
        userBookId: userBook.id,
      })
      promise.then((newUserBookRead) => {
        dispatch(setUserBookRead({ bookId, userBookRead: newUserBookRead }));
      });
      return promise;
    } else {
      return insertUserBook({
        client,
        userBook: {
          bookId,
          userId,
          statusId: ReadStatus.id,
          privacySettingId: defaultPrivacySettingId,
          referrerUserId: settings?.referrerUserId,
        } as unknown as UserBookSerializersUserBook,
      }).then(updateUserBookCache).then((newUserBook) => {
        dispatch(setUserBookRead({ bookId, userBookRead: newUserBook.datesRead[0] }));
      }).catch(catchErrorMessage)
    }
  };

  const updateRating = (newRating: number) => {
    if (userBook?.id) {
      // Update
      return updateUserBook({
        client,
        id: userBook.id,
        userBook: {
          ...userBook,
          rating: newRating,
        },
      }).then(updateUserBookCache).catch(catchErrorMessage);
    } else {
      // Create
      return insertUserBook({
        client,
        userBook: {
          bookId,
          userId,
          statusId: ReadStatus.id,
          privacySettingId: defaultPrivacySettingId,
          rating: newRating,
          referrerUserId: settings?.referrerUserId,
        } as unknown as UserBookSerializersUserBook,
      }).then(updateUserBookCache).catch(catchErrorMessage);
    }
  };

  const updateStatus = (statusId: number) => {
    const userLocalDate = new Date().toLocaleDateString('en-CA');
    
    if (userBook?.id) {
      // Update
      return updateUserBook({
        client,
        id: userBook.id,
        userBook: {
          ...userBook,
          statusId: statusId,
        },
        userDate: userLocalDate,
      }).then(updateUserBookCache).catch(catchErrorMessage);
    } else {
      // Create
      return insertUserBook({
        client,
        userBook: {
          bookId,
          privacySettingId: defaultPrivacySettingId,
          userId,
          statusId: statusId,
          referrerUserId: settings?.referrerUserId,
        },
        userDate: userLocalDate,
      }).then(updateUserBookCache).catch(catchErrorMessage);
    }
  };

  const updateEdition = (editionId: number):Promise<UserBookSerializersUserBook> => {
    if (userBook?.id) {
      // Update
      const result = updateUserBook({
        client,
        id: userBook.id,
        userBook: {
          ...userBook,
          editionId,
        },
      })
      result.then(updateUserBookCache)
        .catch(catchErrorMessage);
      return result;
    } else {
      // Create
      const result = insertUserBook({
        client,
        userBook: {
          bookId,
          userId,
          editionId,
          privacySettingId: defaultPrivacySettingId,
          statusId: WantToReadStatus.id,
          referrerUserId: settings?.referrerUserId,
        } as unknown as UserBookSerializersUserBook,
      })
      result.then(updateUserBookCache).catch(catchErrorMessage);
      return result;
    }
  };

  const updatePrivacy = (privacySettingId: PrivacySettingType) => {
    if (userBook?.id) {
      // Update
      return updateUserBook({
        client,
        id: userBook.id,
        userBook: {
          ...userBook,
          privacySettingId,
        },
      }).then(updateUserBookCache).catch(catchErrorMessage);
    } else {
      // Create
      return insertUserBook({
        client,
        userBook: {
          bookId,
          userId,
          statusId: WantToReadStatus.id,
          privacySettingId,
          referrerUserId: settings?.referrerUserId,
        } as unknown as UserBookSerializersUserBook,
      }).then(updateUserBookCache).catch(catchErrorMessage);
    }
  };

  const deleteUserBook = () => {
    return destroyUserBook(userBook.id, client).then((newUserBook) => {
      if (bookId) {
        dispatch(removeUserBook({ bookId: newUserBook.bookId }));
        dispatch(bookButtonActions.removeUserBook({ bookId: newUserBook.bookId }));
      }
      return newUserBook;
    })
  };

  return {
    deleteUserBook,
    insertDateRead,
    userBook,
    updateEdition,
    updateRating,
    updateStatus,
    updatePrivacy,
  };
}
